import React from 'react';
import Papa from 'papaparse';
import { axios } from '../../../../services/axios';
import { downloadCSV } from '../../../../utils/utils';
import { useSelector } from 'react-redux';

const DEFAULT_BULK_UPDATE_STATUS = {
    apiErrors: '',
};
const ERROR_MESSAGE = 'Something went wrong. Please try again later.';
const FILENAME = 'FailedUploadingData.csv';

const FIELDS_TO_EXPORT = {
    o365UserPrincipalName: 'UPN',
    displayName: 'Display Name',
    telephoneNumber: 'Telephone Number',
    dialPlan: 'Dial Plan',
    voicePolicy: 'Voice Policy',
    usageLocation: 'Usage Location',
    managedBy: 'Managed By (TCAP/Other)',
    plan: 'Plan',
    attribute1: 'Attribute 1',
    attribute2: 'Attribute 2',
    attribute3: 'Attribute 3',
    apiErrors: 'Errors',
};

export default function useBulkUpdate() {
    const [error, setError] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [succeed, setSucceed] = React.useState([]);
    const [counter, setCounter] = React.useState(0);
    const [errorCounter, setErrorCounter] = React.useState(0);
    const [uploadPayloads, setUploadPayloads] = React.useState([]);
    const isIgnoredFilter = (v) => v.ignored;
    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });

    //add more UI states to BE payloads.
    const transformSubmitData = (payloads) => {
        return payloads
            .filter((v) => !isIgnoredFilter(v))
            .map((payload) => ({
                ...DEFAULT_BULK_UPDATE_STATUS,
                ...payload,
            }));
    };

    //entry point to start API call
    const processSubmission = async (payloads) => {
        setLoading(true);
        const finalPayloads = transformSubmitData(payloads);
        handleBulkUpdate(finalPayloads);
    };

    //reset UI
    const handleReset = () => {
        setCounter(0);
        setErrorCounter(0);
        setSucceed([]);
        setError([]);
    };

    //after API calls, set UI State
    const handleFinishBulkUpdate = ({ errors, responses }) => {
        setError(errors);
        setSucceed(responses);
    };

    //handle api calls
    const handleBulkUpdate = async (payloads) => {
        const errors = [];
        const responses = [];
        setUploadPayloads(payloads);

        // Helper function to delay execution
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        // Process payloads sequentially with delay
        for (let index = 0; index < payloads.length; index++) {
            const payload = payloads[index];

            try {
                console.log(payload);
                let response;

                // Add 3 second delay between each API call (except for the first one)
                if (index > 0) {
                    await delay(3000);
                }

                if (payload.type === 'platformUserUpdate') {
                    response = await axios.put(
                        '/PlatformUser',
                        payload.payload,
                    );
                }
                // Handle profile operations
                if (payload.type === 'deleteProfile') {
                    const { UPN, ...restPayload } = payload.queryParams || {};
                    response = await axios.delete('/UserProfile', {
                        params: restPayload,
                    });
                    if (response.status === 200) {
                        response = { data: { UPN: UPN, status: 'deleted' } };
                    }
                }
                if (payload.type === 'createProfile') {
                    const { UPN, ...restPayload } = payload.payload || {};
                    response = await axios.post('/UserProfile', restPayload);
                    if (response.status === 200) {
                        response = { data: { UPN: UPN, status: 'OK' } };
                    }
                }
                if (payload.type === 'updateProfile') {
                    const { UPN, ...restPayload } = payload.payload || {};
                    response = await axios.put('/UserProfile', restPayload);
                    if (response.status === 200) {
                        response = { data: { UPN: UPN, status: 'OK' } };
                    }
                }
                if (payload.type === 'create8x8Licenses') {
                    const { UPN, ...restPayload } = payload.payload || {};
                    response = await axios.post('/UserProfile', restPayload);
                    if (response.status === 200) {
                        response = { data: { UPN: UPN, status: 'OK' } };
                    }
                }
                if (payload.type === 'update8x8Licenses') {
                    const { UPN, ...restPayload } = payload.payload || {};
                    response = await axios.put('/UserProfile', restPayload);
                    if (response.status === 200) {
                        response = { data: { UPN: UPN, status: 'OK' } };
                    }
                }

                responses.push({
                    ...response,
                });
            } catch (e) {
                //put to failed table
                errors.push({
                    ...payload,
                    apiErrors:
                        `${e?.message}\n${e?.response.data?.message}\n${e?.response?.status}` ||
                        ERROR_MESSAGE,
                });
                //count as error
                setErrorCounter((prev) => prev + 1);
            } finally {
                //set UI counter
                setCounter((prev) => prev + 1);
                console.log(responses);
                if (index === payloads.length - 1) {
                    //setState for Failed & Succeed table
                    handleFinishBulkUpdate({ errors, responses });
                }
            }
        }
    };

    /**
     * handle UI button - start api call when payload is ready.
     */
    const handlePayloadChange = React.useCallback((payloads) => {
        processSubmission(payloads);
    }, []);

    /**
     * handle UI button - export API calls that failed to CSV.
     */
    const handleExportFailedData = () => {
        const failedData = transformJSONToCSV(error);
        const res = Papa.unparse(failedData);
        downloadCSV(res, FILENAME);
    };

    /**
     * transform BE JSON field to more human readable CSV heading columns.
     * @param {*} data
     * @returns
     */
    const transformJSONToCSV = (data) => {
        return data.map((user) => {
            return Object.keys(FIELDS_TO_EXPORT).reduce((acc, key) => {
                const value = user[key];
                const newKey = FIELDS_TO_EXPORT[key];
                acc[newKey] = value;
                if (key === 'managedBy') {
                    acc[newKey] = value === 0 ? 'TCAP' : 'Other';
                }
                return acc;
            }, {});
        });
    };

    return {
        handlePayloadChange,
        loading,
        counter,
        errorCounter,
        error,
        succeed,
        handleReset,
        handleExportFailedData,
        total: uploadPayloads.length,
    };
}
